import {
  EPaymentInstrument,
  EWalletConfigMode,
  ICardTokenizationOptions,
  IUseDigitalWallets,
  TPaymentInstrument,
} from './wallet-types';
import { PROVIDER_NAMES } from '../../../variables/digital-wallets';
import store from '../../../state/store';
import { isEmbeddedWalletConfigActive } from './wallet-visibility';

export const getProviderName: IUseDigitalWallets['getProviderName'] = (
  providerName,
  type = 'full',
): string => {
  const provider = PROVIDER_NAMES?.[providerName];

  return provider ? provider[type] : '';
};

export const checkIsDigitalWallet = (m: TPaymentInstrument) => {
  return m === EPaymentInstrument.GooglePay || m === EPaymentInstrument.ApplePay;
};

/**
 * PRX-23109 (Revision 2, N1): is the currently selected Credit Card
 * gateway's own configuration the old, toggle-inside-Credit-Card way
 * (`Embedded`) or the newer, separate-payment-method way (`Separate`)?
 * Built directly on `cardTokenizationOptions` -- already computed and
 * stored per gateway selection by change-pay-info.js's
 * `getDigitalWalletDataByOptions` -- rather than re-deriving a parallel
 * signal: a Credit Card gateway that lists at least one supported wallet
 * provider is configured the old, embedded way.
 */
export const getWalletConfigMode = (
  cardTokenizationOptions: ICardTokenizationOptions | null,
): EWalletConfigMode =>
  cardTokenizationOptions?.google_pay || cardTokenizationOptions?.apple_pay
    ? EWalletConfigMode.Embedded
    : EWalletConfigMode.Separate;

/**
 * PRX-23109 (user-reported): the wallet instrument genuinely in play right
 * now, for EITHER the separate-payment-method path (`paymentInstrument`)
 * or the embedded, toggle-inside-Credit-Card path (`embeddedToggleInstrument`,
 * once its own toggle points at a wallet) -- mirrors
 * useDigitalWallets.ts's own `effectiveWalletInstrument`. `paymentInstrument`
 * alone is always Card for a Credit Card selection, so reading wallet data
 * by it alone silently saw "no wallet" for the embedded configuration.
 */
export const getActiveWalletInstrument = (): TPaymentInstrument => {
  const state = store.getState();
  const { currentGateway, profileInfo } = state;
  const { paymentInstrument, embeddedToggleInstrument, cardTokenizationOptions } =
    state.payInfo.digitalWallets;

  const isPayIn = profileInfo?.transaction_type === 'pay-in';
  const walletConfigMode = getWalletConfigMode(cardTokenizationOptions);
  const isEmbeddedActive =
    isEmbeddedWalletConfigActive(walletConfigMode, currentGateway?.payment_method_key, isPayIn) &&
    checkIsDigitalWallet(embeddedToggleInstrument);

  return isEmbeddedActive ? embeddedToggleInstrument : paymentInstrument;
};

export const getWalletCurrencyLimits = (currency: string) => {
  const { cardTokenizationOptions } = store.getState().payInfo.digitalWallets;
  const instrument = getActiveWalletInstrument();
  if (!checkIsDigitalWallet(instrument)) return null;

  return cardTokenizationOptions?.[instrument]?.currency_list?.[currency] ?? null;
};

const PAGE_ORIGIN_STORAGE_KEY = 'page_origin';

export const getPageOrigin = (): string => {
  try {
    const cached = sessionStorage.getItem(PAGE_ORIGIN_STORAGE_KEY);
    if (cached) return cached;
  } catch {}

  const origin = window?.CASHIER_CONFIG?.refererDomain || window.location.host;

  try {
    sessionStorage.setItem(PAGE_ORIGIN_STORAGE_KEY, origin);
  } catch {}

  return origin;
};
